/* eslint-disable sonarjs/cognitive-complexity */
import { G } from '@mobily/ts-belt';
import InfoIcon from '@mui/icons-material/Info';
import { Box, Divider } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  BooleanInput,
  NumberInput,
  number,
  RaRecord,
  required,
  SelectInput,
  TextInput,
  useGetList,
  useRecordContext,
  useTranslate,
  Validator,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { B2bContract } from '@Common/types/bo/b2b/contracts';
import { getContractTypeDefinition } from '@Common/types/bo/b2b/contracts/contract_type_definitions';
import { GetB2bEmployeesOneResponse } from '@Common/types/bo/b2b/employees';
import { BoListQueryBase } from '@Common/types/bo/bo_list_query';

import { IdArrayInput, ShowSection } from '@sh4res/bo-common/components';
import {
  genderChoices,
  taxResidenceCodeChoices,
} from '@sh4res/bo-common/components/B2bEmployee/choices';
import {
  getDefaultRetirementDate,
  wideInputStyle,
} from '@sh4res/bo-common/components/B2bEmployee/common';
import { B2bCompanySelectInput } from '@sh4res/bo-common/fields';
import { DateInput } from '@sh4res/bo-common/fields/DateInput';
import { useResourceBasePath, useValueChanged } from '@sh4res/bo-common/hooks';
import {
  useCountryCodeChoicesByAdjective,
  useCountryCodeChoicesByName,
} from '@sh4res/bo-common/utils/countryChoices';
import { tryToDateString } from '@sh4res/bo-common/utils/date';
import { isBoExternal } from '@sh4res/bo-common/utils/env';
import {
  dobValidation,
  erAffiliationRequiresTaigaIdValidation,
  taxNumberValidation,
} from '@sh4res/bo-common/utils/validators/b2b';
import { useNIRValidator } from '@sh4res/bo-common/validators/nir-form-validator';

const dividerStyle = { width: '100%', margin: '0 0 10px 0' };

interface ContractFromApiForStep extends RaRecord {
  contract_number: string;
}

interface GetB2bContractsForEmployeeStepQuery extends BoListQueryBase {
  filter?: {
    b2b_company_id?: number;
    is_active?: boolean;
    id?: number;
    query?: string;
  };
}

export const PersonalInformationStep = ({
  allowCompanyId = false,
  companyId: companyIdFromProps,
  recordId,
  ntt,
  testId,
}: {
  allowCompanyId: boolean;
  companyId?: number;
  recordId?: number;
  ntt?: string | null;
  testId?: string;
}) => {
  const formContext = useFormContext();
  const { setValue, trigger } = formContext;
  const [
    dobWatch,
    retirementDateWatch,
    companyIdFromForm,
    genderWatch,
    postcodeOfBirthWatch,
    contractIds,
    taigaIdWatch,
  ] = useWatch({
    name: [
      'date_of_birth',
      'retirement_date',
      'company_id',
      'gender',
      'postcode_of_birth',
      'contract_ids',
      'id_external_taiga',
    ],
  });

  const nirNotYet = useWatch({ name: 'nir_not_yet', defaultValue: G.isNotNullable(ntt) });

  const translate = useTranslate();
  const nirValidator = useNIRValidator();
  const countryCodeChoicesByAdjective = useCountryCodeChoicesByAdjective();
  const countryCodeChoicesByName = useCountryCodeChoicesByName();
  const record = useRecordContext<GetB2bEmployeesOneResponse>();

  const companyId = allowCompanyId ? companyIdFromForm : (companyIdFromProps ?? companyIdFromForm);

  const contractSearchFilter = useMemo(
    () => (companyId ? { b2b_company_id: companyId, is_active: true } : { id: -1 }),
    [companyId],
  );

  const previousCompanyIdRef = useRef(companyId);

  // update retirement date when date of birth changes only if it was not manually changed
  useEffect(() => {
    if (
      previousCompanyIdRef.current !== companyId &&
      (previousCompanyIdRef.current !== undefined || companyId !== undefined)
    ) {
      setValue('contract_ids', [], { shouldValidate: true, shouldDirty: true });
    }
    previousCompanyIdRef.current = companyId;
  }, [companyId, setValue]);

  useEffect(() => {
    if (nirNotYet === true) {
      setValue('nir', null);
    } else if (nirNotYet === false) {
      setValue('ntt', null);
    }
  }, [nirNotYet, setValue]);

  useValueChanged(dobWatch, (value?: string, previous?: string) => {
    if (!value || !previous) {
      return;
    }

    // Only update the retirement date if it still matches the default derived from the previous dob
    if (tryToDateString(retirementDateWatch) === getDefaultRetirementDate(previous)) {
      setValue('retirement_date', getDefaultRetirementDate(value));
    }
  });

  // Re-trigger NIR validation when dependent fields change
  useEffect(() => {
    if (!nirNotYet) {
      trigger('nir');
    }
  }, [genderWatch, dobWatch, postcodeOfBirthWatch, nirNotYet, trigger]);

  const resourceBasePath = useResourceBasePath();

  const { data: contracts } = useGetList<B2bContract>(
    `${resourceBasePath}contracts`,
    {
      filter: { id: contractIds },
      pagination: { page: 1, perPage: 100 },
    },
    { enabled: G.isNotNullable(contractIds) },
  );

  useEffect(() => {
    if (!G.isUndefined(recordId)) {
      return;
    }
    trigger('contract_ids');
  }, [taigaIdWatch, contractIds, contracts, recordId, trigger]);

  useEffect(() => {
    if (G.isNotNullable(contracts) && contracts.length > 0) {
      setValue(
        'allows_manual_onboarding',
        contracts.some(
          (contract) =>
            getContractTypeDefinition(contract.contract_type).employees.allowsManualOnboarding,
        ),
      );
    }
  }, [contracts, setValue]);

  const getOptionalValidation = useCallback(
    (source: keyof GetB2bEmployeesOneResponse, extraValidators: Validator[] = []): Validator[] => {
      const isRequired = !record || G.isNotNullable(record[source]) || isBoExternal();

      return isRequired ? [required(), ...extraValidators] : extraValidators;
    },
    [record],
  );
  return (
    <Box>
      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.employees.labels.identity"
        noCard={true}>
        {allowCompanyId && (
          <>
            <B2bCompanySelectInput
              source="company_id"
              validate={[required()]}
              isRequired
              testId={testId ? `${testId}_CompanySelect` : undefined}
            />
            <Divider sx={dividerStyle} />
          </>
        )}
        {G.isUndefined(recordId) && (
          <IdArrayInput<GetB2bContractsForEmployeeStepQuery, ContractFromApiForStep>
            data-testid={testId ? `${testId}_ContractIdsInput` : undefined}
            key={companyId || 'no-company'}
            source="contract_ids"
            reference={`${resourceBasePath}contracts`}
            addIdLabel="resources.bo_common.b2b.employees.fields.contract_ids"
            getLabel={(contract: ContractFromApiForStep) =>
              contract.contract_number || `ID: ${contract.id}`
            }
            searchLimit={50}
            searchFilter={contractSearchFilter}
            searchTarget="query"
            disabled={!companyId}
            fullWidth
            isRequired
            validate={[required(), erAffiliationRequiresTaigaIdValidation(contracts)]}
            fetchChoicesOnMountIfFilterPresent={true}
            displayPrefetchedOptionsWithoutSearchText={true}
          />
        )}
        {!nirNotYet && (
          <TextInput
            source="nir"
            validate={[required(), nirValidator]}
            isRequired
            helperText={
              <>
                <InfoIcon sx={{ height: '12px', verticalAlign: 'middle' }} />
                {translate('resources.bo_common.b2b.employees.labels.nir_helper')}
              </>
            }
            defaultValue={null}
            data-testid={testId ? `${testId}_NirInput` : undefined}
          />
        )}
        <BooleanInput
          source="nir_not_yet"
          label="resources.bo_common.b2b.employees.labels.nir_not_yet"
          defaultValue={nirNotYet}
          data-testid={testId ? `${testId}_NirNotYetInput` : undefined}
        />
        {nirNotYet && (
          <TextInput
            source="ntt"
            validate={[required()]}
            isRequired
            defaultValue={null}
            data-testid={testId ? `${testId}_NttInput` : undefined}
          />
        )}
        <TextInput
          source="firstname"
          validate={[required()]}
          isRequired
          data-testid={testId ? `${testId}_FirstnameInput` : undefined}
        />
        <TextInput
          source="lastname"
          validate={[required()]}
          isRequired
          data-testid={testId ? `${testId}_LastnameInput` : undefined}
        />
        <TextInput
          source="name_in_use"
          defaultValue={null}
          data-testid={testId ? `${testId}_NameInUseInput` : undefined}
        />
        <SelectInput
          source="gender"
          validate={[required()]}
          isRequired
          choices={genderChoices}
          data-testid={testId ? `${testId}_GenderSelect` : undefined}
        />
        <DateInput
          source="date_of_birth"
          validate={getOptionalValidation('date_of_birth', [dobValidation()])}
          data-testid={testId ? `${testId}_DateOfBirthInput` : undefined}
        />
        <TextInput
          source="place_of_birth"
          validate={getOptionalValidation('place_of_birth')}
          data-testid={testId ? `${testId}_PlaceOfBirthInput` : undefined}
        />
        <SelectInput
          source="country_of_birth"
          defaultValue={null}
          choices={countryCodeChoicesByName}
          validate={getOptionalValidation('country_of_birth')}
          data-testid={testId ? `${testId}_CountryOfBirthSelect` : undefined}
        />
        <TextInput
          source="postcode_of_birth"
          validate={[required()]}
          data-testid={testId ? `${testId}_PostcodeOfBirthInput` : undefined}
        />
        <SelectInput
          source="nationality"
          choices={countryCodeChoicesByAdjective}
          validate={getOptionalValidation('nationality')}
          data-testid={testId ? `${testId}_NationalitySelect` : undefined}
        />
        <TextInput
          source="registration_number"
          defaultValue={null}
          data-testid={testId ? `${testId}_RegistrationNumberInput` : undefined}
        />
        <NumberInput
          source="id_external_taiga"
          defaultValue={null}
          data-testid={testId ? `${testId}_IdExternalTaigaInput` : undefined}
        />
        <TaxInfoSection testId={testId} />
      </ShowSection>
    </Box>
  );
};

const TaxInfoSection = ({ testId }: { testId?: string }) => {
  const ninNonYet = useWatch({ name: 'nin_not_yet' });

  return (
    <ShowSection
      leftAligned
      label="resources.bo_common.b2b.employees.labels.tax_info"
      noCard={true}>
      <SelectInput
        source="tax_residence_code"
        choices={taxResidenceCodeChoices}
        validate={[required()]}
        data-testid={testId ? `${testId}_TaxResidenceCodeSelect` : undefined}
      />

      {!ninNonYet && (
        <TextInput
          sx={wideInputStyle}
          source="tax_number"
          validate={[
            number(),
            taxNumberValidation('resources.bo_common.b2b.employees.validation.tax_number'),
          ]}
          defaultValue={null}
          data-testid={testId ? `${testId}_TaxNumberInput` : undefined}
        />
      )}
      <BooleanInput
        source="nin_not_yet"
        label="resources.bo_common.b2b.employees.labels.nin_not_yet"
        data-testid={testId ? `${testId}_NinNotYetInput` : undefined}
      />
    </ShowSection>
  );
};
